/**
 * An organization's own provider connections and model routing. These are the
 * platform operator's screens, rendered against the organization's own endpoints
 * rather than reimplemented: the request was for the same capability, and two
 * implementations of a credential form is how they drift apart.
 */
import { useMemo, type ReactNode } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { OrgLayout } from "../../../components/Shell";
import { PageHeader, SectionTabs } from "../../../components/ui";
import { AiAdminScopeProvider, AiProvidersPage, AiRoutingPage } from "../../ai-admin/AiAdminPage";
import { orgAiAdmin } from "../../ai-admin/api";
import "../../ai-admin/ai-admin.css";

function OrgAiAdminFrame({ org, children }: { org: string; children: ReactNode }) {
  const client = useMemo(() => orgAiAdmin(org), [org]);
  return (
    <OrgLayout orgId={org}>
      <div className="page ai-admin">
        <PageHeader title="AI providers &amp; routing" />
        <SectionTabs label="AI setup">
          <Link to="/orgs/$org/ai" params={{ org }} activeOptions={{ exact: true }}>General</Link>
          <Link to="/orgs/$org/ai/providers" params={{ org }}>Providers</Link>
          <Link to="/orgs/$org/ai/routing" params={{ org }}>Routing</Link>
        </SectionTabs>
        <AiAdminScopeProvider client={client} scope="organization">
          {children}
        </AiAdminScopeProvider>
      </div>
    </OrgLayout>
  );
}

export function OrgAiProvidersPage() {
  const { org } = useParams({ strict: false }) as { org: string };
  return (
    <OrgAiAdminFrame org={org}>
      <AiProvidersPage />
    </OrgAiAdminFrame>
  );
}

export function OrgAiRoutingPage() {
  const { org } = useParams({ strict: false }) as { org: string };
  return (
    <OrgAiAdminFrame org={org}>
      <AiRoutingPage />
    </OrgAiAdminFrame>
  );
}
